import { useRef } from 'react';
import { Input } from '../input/input';
import { Typography } from '../typography/typography';
import * as styles from './colorPicker.css';
import { clsx } from 'clsx';

type ColorPickerProps = {
  value: string;
  onChange: (hex: string) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
};

const isValidHex = (value: string): boolean =>
  /^[0-9A-F]{6}$/.test(value.toUpperCase());

export const ColorPicker = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
}: ColorPickerProps) => {
  const nativeInputRef = useRef<HTMLInputElement>(null);

  const handleSwatchClick = () => {
    if (disabled) {
      return;
    }

    nativeInputRef.current?.click();
  };

  const handleNativeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hex = e.target.value.replace('#', '').toUpperCase();
    onChange(hex);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase();

    if (!isValidHex(raw)) {
      return;
    }

    onChange(raw);
  };

  return (
    <div>
      {label && (
        <Typography variant="span" size="small" color="N-80">
          {label}
        </Typography>
      )}
      <div className={styles.wrapper}>
        <button
          type="button"
          className={clsx(styles.swatch, disabled && styles.swatchDisabled)}
          style={{ backgroundColor: `#${value}` }}
          onClick={handleSwatchClick}
          disabled={disabled}
          aria-label="Open color picker"
        />
        <input
          ref={nativeInputRef}
          type="color"
          className={styles.hiddenInput}
          value={`#${value}`}
          onChange={handleNativeChange}
          disabled={disabled}
          tabIndex={-1}
        />
        <div className={styles.inputWrapper}>
          <Input
            startCta={
              <Typography
                variant="span"
                color="N-100"
                className={styles.hashPrefix}
              >
                #
              </Typography>
            }
            value={value}
            onChange={(e) => {
              const clean = e.target.value
                .replace(/#/g, '')
                .toUpperCase()
                .slice(0, 6);
              onChange(clean);
            }}
            onBlur={handleInputBlur}
            maxLength={7}
            error={error}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};
